import { Typography } from "@/components/common/Typography/Typography"
import { Layout } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { URLS } from "@/utils/urls.const"
import { AlertTriangle, FileQuestion, Lock } from "lucide-react"
import React from "react"
import { useNavigate } from "react-router"

type ErrorPageVariant = "not-found" | "feature-unavailable" | "error"

interface ErrorPageProps {
  variant?: ErrorPageVariant
  withLayout?: boolean
}

const VARIANT_CONFIG = {
  "not-found": {
    icon: FileQuestion,
    titleKey: "ERROR_PAGE/NOT_FOUND_TITLE",
    descriptionKey: "ERROR_PAGE/NOT_FOUND_DESCRIPTION",
    code: "404",
  },
  "feature-unavailable": {
    icon: Lock,
    titleKey: "ERROR_PAGE/FEATURE_UNAVAILABLE_TITLE",
    descriptionKey: "ERROR_PAGE/FEATURE_UNAVAILABLE_DESCRIPTION",
    code: null,
  },
  error: {
    icon: AlertTriangle,
    titleKey: "ERROR_PAGE/ERROR_TITLE",
    descriptionKey: "ERROR_PAGE/ERROR_DESCRIPTION",
    code: null,
  },
} as const

export const ErrorPage: React.FC<ErrorPageProps> = ({
  variant = "not-found",
  withLayout = false,
}) => {
  const { translate } = useTranslation()
  const navigate = useNavigate()

  const config = VARIANT_CONFIG[variant]
  const Icon = config.icon

  const content = (
    <div className="flex h-full items-center justify-center bg-grey3">
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-4 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-grey2">
            <Icon className="h-8 w-8 text-yellow1" strokeWidth={1.5} />
          </div>
        </div>

        {config.code ? (
          <Typography
            as="span"
            variant="titleL"
            className="font-bold text-yellow1"
          >
            {config.code}
          </Typography>
        ) : null}

        <Typography as="h1" variant="titleM" className="mb-2">
          {translate(config.titleKey)}
        </Typography>

        <Typography variant="textS" className="mb-6 text-grey5">
          {translate(config.descriptionKey)}
        </Typography>

        <div className="flex gap-3">
          {variant === "error" ? (
            <Button variant="outline" onClick={() => navigate(-1)}>
              {translate("ERROR_PAGE/GO_BACK")}
            </Button>
          ) : null}
          <Button onClick={() => navigate("/")}>
            {translate("ERROR_PAGE/GO_HOME")}
          </Button>
          {variant !== "error" ? (
            <Button
              variant={"outline"}
              onClick={() => window.location.assign(URLS.INFO_BOX.HELP)}
            >
              {translate("ERROR_PAGE/CONTACT_US")}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )

  if (withLayout) {
    return <Layout>{content}</Layout>
  }

  return content
}
